import React, { useCallback, useMemo, useState } from 'react';
// Outlet will be rendered by PermissionWrapper
// import { Outlet } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import PermissionWrapper from './PermissionWrapper'; // Import PermissionWrapper
import useMobile from '../../hooks/useMobile';
import { Breadcrumb } from '../ui/Breadcrumb';
import LocationAwareErrorBoundary from '../ErrorBoundary/LocationAwareErrorBoundary';
import { TableEnhancer } from '../ui/TableEnhancer';
import { clsx } from 'clsx';
import { useChangelog } from '@/hooks/useChangelog';
import { ChangelogModal } from '@/components/changelog/ChangelogModal';

const MainLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 768,
  );

  const { showChangelog, sections, dismissChangelog } = useChangelog();

  // When the breakpoint changes (resize), reset sidebar to the default for that mode
  const handleBreakpointChange = useCallback((mobile: boolean) => {
    setSidebarOpen(!mobile);
  }, []);

  const isMobile = useMobile(768, handleBreakpointChange);

  const toggleSidebar = () => {
    setSidebarOpen((previous) => !previous);
  };

  const sidebarContainerClasses = useMemo(() => {
    const widthClass = isMobile
      ? 'w-72'
      : sidebarOpen
        ? 'w-72'
        : 'w-16';

    const transformClass = isMobile
      ? sidebarOpen
        ? 'translate-x-0'
        : '-translate-x-full'
      : 'translate-x-0';

    const baseSurfaceClass = isMobile
      ? 'bg-white shadow-2xl'
      : 'border-r border-gray-200 bg-gradient-to-b from-gray-50 to-gray-100';

    return clsx(
      'fixed left-0 top-16 h-[calc(100vh-4rem)] z-40 transition-[width,transform] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]',
      widthClass,
      transformClass,
      baseSurfaceClass,
    );
  }, [isMobile, sidebarOpen]);

  const mainContentClasses = useMemo(
    () =>
      clsx(
        'flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 transition-[margin] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]',
        !isMobile && (sidebarOpen ? 'md:ml-72' : 'md:ml-16'),
      ),
    [isMobile, sidebarOpen],
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <TableEnhancer />
      <ChangelogModal
        isOpen={showChangelog}
        onClose={dismissChangelog}
        sections={sections}
      />
      {/* Fixed Header */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      </div>

      <div className="flex flex-1 pt-16"> {/* pt-16 to account for fixed header height */}
        {/* Fixed Sidebar */}
        <div className={sidebarContainerClasses}>
          <Sidebar isOpen={sidebarOpen} onToggle={toggleSidebar} showLogoSection={isMobile} />
        </div>

        {isMobile && sidebarOpen && (
          <div
            className="fixed inset-0 top-16 z-30 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] animate-in fade-in"
            onClick={toggleSidebar}
            aria-hidden="true"
          />
        )}

        <main className={mainContentClasses}>
          <Breadcrumb />
          <LocationAwareErrorBoundary>
            <PermissionWrapper /> {/* Use PermissionWrapper here */}
          </LocationAwareErrorBoundary>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
